import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { EMPTY_PRODUCT, gbp } from "./constants";
import ListingForm from "./ListingForm";
import PhotoSwapDialog from "./PhotoSwapDialog";

export default function AdminListings() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [swapping, setSwapping] = useState(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [toast, setToast] = useState("");
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkDeleting, setBulkDeleting] = useState(false);

    const load = async () => {
        const { data } = await api.get("/products/admin/all");
        setItems(data.items);
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        return items.filter((p) => {
            if (statusFilter && p.status !== statusFilter) return false;
            if (search) {
                const q = search.toLowerCase();
                return (p.title + " " + p.designer + " " + (p.sku || "")).toLowerCase().includes(q);
            }
            return true;
        });
    }, [items, search, statusFilter]);

    const save = async (product) => {
        const body = {
            ...product,
            price: Number(product.price),
            original_price: product.original_price ? Number(product.original_price) : null,
            images: Array.isArray(product.images) ? product.images : [],
            tags: typeof product.tags === "string" ? product.tags.split(",").map((s) => s.trim()).filter(Boolean) : product.tags,
        };
        let saved;
        if (product.id) {
            const { data } = await api.put(`/products/${product.id}`, body);
            saved = data;
            // Update in place so the row reflects the edit immediately
            setItems((prev) => prev.map((p) => p.id === saved.id ? saved : p));
        } else {
            const { data } = await api.post("/products", body);
            saved = data;
            // Optimistically prepend so the new listing appears at the top of the table
            setItems((prev) => [saved, ...prev.filter((p) => p.id !== saved.id)]);
        }
        setEditing(null);
        setToast(`${saved.title} — ${saved.status === "live" ? "live in the shop" : `saved as ${saved.status}`}`);
        // Refetch in the background to stay in sync (drafts, sorting, sku, etc.)
        load().catch(() => {});
        setTimeout(() => setToast(""), 4000);
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this listing?")) return;
        await api.delete(`/products/${id}`);
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
        await load();
    };

    const toggleOne = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const filteredIds = useMemo(() => filtered.map((p) => p.id), [filtered]);
    const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));
    const someFilteredSelected = filteredIds.some((id) => selectedIds.has(id));

    const toggleAllFiltered = () => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                filteredIds.forEach((id) => next.delete(id));
            } else {
                filteredIds.forEach((id) => next.add(id));
            }
            return next;
        });
    };

    const clearSelection = () => setSelectedIds(new Set());

    const bulkDelete = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        if (!window.confirm(`Permanently delete ${ids.length} listing${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
        setBulkDeleting(true);
        try {
            const { data } = await api.post("/products/bulk-delete", { ids });
            setItems((prev) => prev.filter((p) => !selectedIds.has(p.id)));
            setSelectedIds(new Set());
            setToast(`${data.deleted} listing${data.deleted === 1 ? "" : "s"} deleted`);
            setTimeout(() => setToast(""), 4000);
            load().catch(() => {});
        } catch (e) {
            window.alert(e?.response?.data?.detail || "Bulk delete failed. Please try again.");
        } finally {
            setBulkDeleting(false);
        }
    };

    const saveJustImages = async (product, images) => {
        // Send the full product back (backend PUT requires the ProductIn schema)
        // to swap ONLY the images without opening the wizard.
        const body = {
            title: product.title,
            designer: product.designer,
            category: product.category,
            subcategory: product.subcategory || "",
            size: product.size,
            color: product.color || "",
            condition: product.condition,
            new_with_tags: !!product.new_with_tags,
            description: product.description || "",
            measurements: product.measurements || "",
            material: product.material || "",
            hardware: product.hardware || "",
            interior: product.interior || "",
            country_of_origin: product.country_of_origin || "",
            serial_number: product.serial_number || "",
            accompanied_by: product.accompanied_by || [],
            price: Number(product.price),
            original_price: product.original_price ? Number(product.original_price) : null,
            images,
            edit_slug: product.edit_slug || null,
            status: product.status,
            authenticated: product.authenticated !== false,
            tags: product.tags || [],
            sku: product.sku || null,
        };
        const { data } = await api.put(`/products/${product.id}`, body);
        setItems((prev) => prev.map((p) => p.id === data.id ? data : p));
        setToast(`${data.title} — ${images.length} photo${images.length === 1 ? "" : "s"} saved`);
        setTimeout(() => setToast(""), 4000);
    };

    if (editing) return <ListingForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />;

    return (
        <div data-testid="admin-listings">
            {toast && (
                <div data-testid="admin-listings-toast" style={{
                    marginBottom: 16, padding: "12px 16px",
                    background: "var(--cocoa)", color: "var(--ivory)",
                    fontFamily: "var(--font-serif)", fontStyle: "italic",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                    <span>✓ {toast}</span>
                    <button onClick={() => setToast("")} style={{ background: "transparent", border: "none", color: "var(--ivory)", cursor: "pointer", fontSize: 16 }} aria-label="Dismiss">×</button>
                </div>
            )}
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
                <input className="input" placeholder="Search title, designer, SKU" value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 300 }} data-testid="admin-listings-search" />
                <select className="input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ maxWidth: 180 }}>
                    <option value="">All statuses</option>
                    {["draft","live","reserved","sold","archived"].map((s) => <option key={s}>{s}</option>)}
                </select>
                <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                    <div style={{ fontFamily: "var(--font-serif)" }}>{filtered.length} of {items.length}</div>
                    <button className="btn" onClick={() => setEditing({ ...EMPTY_PRODUCT })} data-testid="admin-new-listing">+ New listing</button>
                </div>
            </div>
            {selectedIds.size > 0 && (
                <div data-testid="admin-bulk-bar" style={{
                    marginBottom: 12, padding: "12px 16px",
                    background: "var(--parchment-soft)", border: "1px solid var(--cocoa)",
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
                    flexWrap: "wrap",
                }}>
                    <div style={{ fontFamily: "var(--font-serif)", color: "var(--cocoa)" }}>
                        <strong data-testid="admin-bulk-count">{selectedIds.size}</strong> selected
                        <button
                            onClick={clearSelection}
                            data-testid="admin-bulk-clear"
                            style={{
                                marginLeft: 12, background: "transparent", border: "none",
                                textDecoration: "underline", cursor: "pointer",
                                color: "var(--cocoa)", opacity: 0.7,
                                fontFamily: "inherit", fontSize: "inherit",
                            }}
                        >
                            Clear
                        </button>
                    </div>
                    <button
                        onClick={bulkDelete}
                        disabled={bulkDeleting}
                        data-testid="admin-bulk-delete"
                        className="btn"
                        style={{ background: "#7a2f22", borderColor: "#7a2f22" }}
                    >
                        {bulkDeleting ? "Deleting…" : `Delete ${selectedIds.size} listing${selectedIds.size === 1 ? "" : "s"}`}
                    </button>
                </div>
            )}
            <table className="admin-table">
                <thead>
                    <tr>
                        <th style={{ width: 32, paddingRight: 0 }}>
                            <input
                                type="checkbox"
                                data-testid="admin-select-all"
                                checked={allFilteredSelected}
                                ref={(el) => { if (el) el.indeterminate = !allFilteredSelected && someFilteredSelected; }}
                                onChange={toggleAllFiltered}
                                aria-label="Select all visible listings"
                                style={{ cursor: "pointer", accentColor: "var(--cocoa)" }}
                            />
                        </th>
                        <th></th><th>Piece</th><th>Designer</th><th>SKU</th><th>Price</th><th>Status</th><th></th>
                    </tr>
                </thead>
                <tbody>
                    {filtered.map((p) => (
                        <tr key={p.id} data-selected={selectedIds.has(p.id) || undefined}>
                            <td style={{ paddingRight: 0 }}>
                                <input
                                    type="checkbox"
                                    data-testid={`admin-select-${p.id}`}
                                    checked={selectedIds.has(p.id)}
                                    onChange={() => toggleOne(p.id)}
                                    aria-label={`Select ${p.title}`}
                                    style={{ cursor: "pointer", accentColor: "var(--cocoa)" }}
                                />
                            </td>
                            <td>
                                <button
                                    type="button"
                                    onClick={() => setSwapping(p)}
                                    data-testid={`admin-swap-photos-${p.id}`}
                                    title="Swap photos"
                                    aria-label={`Swap photos for ${p.title}`}
                                    style={{
                                        position: "relative", padding: 0, border: "none",
                                        background: "transparent", cursor: "pointer",
                                        display: "inline-block", lineHeight: 0,
                                    }}
                                    className="admin-row-photo"
                                >
                                    {p.images?.[0] ? (
                                        <img src={p.images[0]} alt="" />
                                    ) : (
                                        <span style={{
                                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                                            width: 56, height: 72, background: "var(--parchment-soft)",
                                            border: "1px dashed var(--sand)",
                                            fontFamily: "var(--font-sans)", fontSize: 9,
                                            letterSpacing: "0.16em", textTransform: "uppercase",
                                            color: "var(--cocoa)", opacity: 0.7,
                                        }}>
                                            + Photo
                                        </span>
                                    )}
                                    <span className="admin-row-photo-hint">Swap</span>
                                </button>
                            </td>
                            <td>
                                <div>{p.title}</div>
                                <div style={{ fontSize: 12, color: "var(--cocoa-soft)" }}>{p.category} · {p.size}</div>
                            </td>
                            <td>{p.designer}</td>
                            <td style={{ fontFamily: "monospace", fontSize: 11 }}>{p.sku || "—"}</td>
                            <td>{gbp(p.price)}</td>
                            <td><span className={`status-pill ${p.status}`}>{p.status}</span></td>
                            <td style={{ textAlign: "right" }}>
                                <button onClick={() => setSwapping(p)} data-testid={`admin-photos-btn-${p.id}`} style={{ background: "transparent", border: "none", cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase" }}>Photos</button>
                                <button onClick={() => setEditing({ ...p, tags: (p.tags || []).join(", ") })} style={{ marginLeft: 12, background: "transparent", border: "none", cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase" }}>Edit</button>
                                <button onClick={() => setEditing({ ...p, id: undefined, sku: "", title: `${p.title} (copy)`, status: "draft", tags: (p.tags || []).join(", ") })} style={{ marginLeft: 12, background: "transparent", border: "none", cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase" }} data-testid={`admin-duplicate-${p.id}`}>Duplicate</button>
                                <button onClick={() => remove(p.id)} style={{ marginLeft: 12, background: "transparent", border: "none", cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: "#7a2f22" }}>Delete</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {swapping && (
                <PhotoSwapDialog
                    product={swapping}
                    onClose={() => setSwapping(null)}
                    onSave={(images) => saveJustImages(swapping, images)}
                />
            )}
        </div>
    );
}
